import { boardToScreen, clamp, type Presence, type Vec, type Viewport } from '@kanvas/board-core';
import { useEffect, useState } from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { useEditor } from '../state/store';

interface Hint {
  userId: string;
  name: string;
  color: string;
  x: number;
  y: number;
  angle: number;
}

const EDGE = 22;

function beacon(peer: Presence): Vec | null {
  if (peer.cursor) return peer.cursor;
  if (peer.viewport) {
    return {
      x: peer.viewport.x + peer.viewport.w / 2,
      y: peer.viewport.y + peer.viewport.h / 2,
    };
  }
  return null;
}

function hintFor(peer: Presence, vp: Viewport): Hint | null {
  const board = beacon(peer);
  if (!board) return null;
  const p = boardToScreen(vp, board);
  if (p.x >= 0 && p.y >= 0 && p.x <= vp.width && p.y <= vp.height) return null;

  const x = clamp(p.x, EDGE, vp.width - EDGE);
  const y = clamp(p.y, EDGE, vp.height - EDGE);
  return {
    userId: peer.userId,
    name: peer.name,
    color: peer.color,
    x,
    y,
    angle: Math.atan2(p.y - y, p.x - x),
  };
}

function sameHints(a: Hint[], b: Hint[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((h, i) => {
    const o = b[i]!;
    return (
      h.userId === o.userId &&
      Math.abs(h.x - o.x) < 1 &&
      Math.abs(h.y - o.y) < 1 &&
      Math.abs(h.angle - o.angle) < 0.05
    );
  });
}

/**
 * Name chips pinned to the screen edge, pointing at peers who have panned
 * or walked their cursor off this view. Clicking one starts follow, which
 * is also how you travel to them.
 */
export function OffscreenPeerHints({ doc }: { doc: BoardDoc }) {
  const followingUserId = useEditor((s) => s.followingUserId);
  const [hints, setHints] = useState<Hint[]>([]);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const vp = useEditor.getState().viewport;
        const following = useEditor.getState().followingUserId;
        const next: Hint[] = [];
        for (const peer of doc.getPresence()) {
          if (peer.userId === following) continue;
          const hint = hintFor(peer, vp);
          if (hint) next.push(hint);
        }
        setHints((prev) => (sameHints(prev, next) ? prev : next));
      });
    };

    update();
    const unsubPresence = doc.subscribePresence(update);
    const unsubViewport = useEditor.subscribe((s, p) => {
      if (s.viewport !== p.viewport || s.followingUserId !== p.followingUserId) update();
    });
    return () => {
      unsubPresence();
      unsubViewport();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [doc, followingUserId]);

  if (hints.length === 0) return null;

  return (
    <div className="offscreen-hints">
      {hints.map((hint) => (
        <button
          key={hint.userId}
          type="button"
          className="offscreen-hint"
          style={{
            left: hint.x,
            top: hint.y,
            ['--hint-color' as string]: hint.color,
          }}
          aria-label={`Следить за ${hint.name}`}
          title={`${hint.name} · следить`}
          onClick={() => useEditor.getState().setUi({ followingUserId: hint.userId })}
        >
          <span
            className="offscreen-hint-arrow"
            style={{ transform: `rotate(${hint.angle}rad)` }}
            aria-hidden="true"
          />
          <span className="offscreen-hint-dot" />
          <span className="offscreen-hint-name">{hint.name}</span>
        </button>
      ))}
    </div>
  );
}
